import React, { useContext, useEffect, useState } from "react";
import { useAuthentication } from "../../contexts/AuthenticationContext";
import { Card, SubHeader, Title } from "./HomeCardStyles";
import PulseLoader from "react-spinners/PulseLoader";
import { RouteConstants } from "../../constants/RouteConstants";
import { useNavigate } from "react-router-dom";
import ConsentFormIcon from "../icons/ConsentFormIcon";
import { useTranslation } from "react-i18next";
import { ThemeContext } from "styled-components";
import { getConsentFormGuardianSigners, getProspectConsentForm } from "../../api/AuthenticatedClient";
import { calcConsentFormSubHeaderText } from "../../util/ConsentFormUtil";
import { ConsentFormGuardianSignersDTO, ProspectConsentFormDTO } from "../../types/ConsentForm";
import useLanguage from "../../hooks/useLanguage";
import { useProspectConsentFormSse } from "../../contexts/ProspectConsetFormSseContext";

const ConsentFormCard: React.FC = () => {
  const { userDetails } = useAuthentication();
  const navigate = useNavigate();
  const { t } = useTranslation("home");
  const { isEn } = useLanguage();
  const Theme = useContext(ThemeContext);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prospectConsentForm, setProspectConsentForm] = useState<ProspectConsentFormDTO>();
  const { signedTs } = useProspectConsentFormSse();
  const [guardianSigners, setGuardianSigners] = useState<ConsentFormGuardianSignersDTO>({
    guardian1: undefined,
    guardian2: undefined,
  });
  const [complete, setComplete] = useState(false);
  const [subHeaderText, setSubHeaderText] = useState<string>("");

  useEffect(() => {
    if (userDetails?.registrationStatusId != 2) {
      setEnabled(true);
    }
  }, [userDetails]);

  useEffect(() => {
    const fetchConsentFormData = async () => {
      const consentForm = await getProspectConsentForm();
      setComplete(!!consentForm.completedTs);
      setProspectConsentForm(consentForm);
      if (consentForm.guardianSignaturesRequired) {
        const guardians = await getConsentFormGuardianSigners();
        setGuardianSigners(guardians);
      }
    };

    if (enabled) {
      setLoading(true);
      fetchConsentFormData().finally(() => {
        setLoading(false);
      });
    }
  }, [enabled]);

  useEffect(() => {
    if (signedTs) {
      setProspectConsentForm({ ...prospectConsentForm, prospectSignedTs: signedTs });
    }
  }, [signedTs]);

  useEffect(() => {
    if (
      !!prospectConsentForm &&
      (!prospectConsentForm.guardianSignaturesRequired || (!!guardianSigners.guardian1 && !!guardianSigners.guardian2))
    ) {
      setSubHeaderText(calcConsentFormSubHeaderText(prospectConsentForm, guardianSigners, isEn));
    }
  }, [prospectConsentForm, guardianSigners, isEn]);

  const onClick = () => {
    if (enabled) {
      navigate(RouteConstants.CONSENT_FORM);
    }
  };

  return (
    <Card onClick={onClick} enabled={enabled} canClick={enabled}>
      <ConsentFormIcon />
      <Title>
        {!enabled || !complete ? t("home:consentFormCardHeaderIncomplete") : t("home:consentFormCardHeaderComplete")}
      </Title>
      {enabled && (
        <SubHeader complete={complete}>
          {loading ? (
            <PulseLoader loading={loading} size={10} color={Theme.colors["mlb-navy"]} />
          ) : (
            <>{subHeaderText}</>
          )}
        </SubHeader>
      )}
    </Card>
  );
};

export default ConsentFormCard;
